/**
 * Bounded-concurrency runner. `run(fn)` resolves with `fn`'s result once a
 * slot is free, so at most `maxConcurrent` calls are ever in flight.
 *
 * Used by the offline pin orchestrator to cap blob downloads, and by batch
 * issue raising to cap concurrent POSTs on a jobsite connection.
 */
export function createSemaphore(maxConcurrent: number) {
  if (maxConcurrent < 1) {
    throw new Error("maxConcurrent must be >= 1");
  }
  let active = 0;
  const waiters: Array<() => void> = [];

  function acquire(): Promise<void> {
    if (active < maxConcurrent) {
      active++;
      return Promise.resolve();
    }
    return new Promise<void>((resolve) => {
      waiters.push(() => {
        active++;
        resolve();
      });
    });
  }

  function release(): void {
    active--;
    const next = waiters.shift();
    if (next) next();
  }

  return async function run<T>(fn: () => Promise<T>): Promise<T> {
    await acquire();
    try {
      return await fn();
    } finally {
      release();
    }
  };
}
